<template>
  <v-container v-if="merchantLoaded">
    <v-layout row wrap mb-2 class="grey lighten-4" align-center>
      <v-flex>
        <h2>
          <router-link to="/merchants">Merchants</router-link>
          <span class="grey--text">&gt;</span>
          {{ merchant.name }} <span class="grey--text">(</span>{{ merchant.id }}<span class="grey--text">)</span>
        </h2>
      </v-flex>
      <v-flex v-if="!editMode" shrink>
        <v-btn small color="primary" @click="editMode = true">
          <v-icon small left>fa-pencil</v-icon>
          Edit Merchant
        </v-btn>
      </v-flex>
      <template v-if="editMode">
        <v-flex shrink>
          <v-btn small color="error" outline @click="cancelAllEdits">
            <v-icon small left>fa-times</v-icon>
            Cancel
          </v-btn>
        </v-flex>
        <v-flex shrink>
          <v-btn small color="primary" @click="modifyMerchant" :disabled="!merchant.is_active">
            Save Merchant Account Changes
          </v-btn>
        </v-flex>
      </template>
      <v-flex shrink>
        <MerchantImageManagment :merchant="merchant" />
      </v-flex>
    </v-layout>

    <!-- View mode: read-only label/value layout -->
    <template v-if="!editMode">
      <v-layout row wrap class="detail-grid">
        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Name:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.name }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Email:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.email }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Contact Name:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.contact_name }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Sales Representative:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.sales_rep_names || '—' }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Processor:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.processor_name || '—' }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Parent Account:</v-flex>
        <v-flex xs6 sm4 class="pa-1">
          <router-link v-if="merchant.parent_account_id" :to="{ name: 'Merchant', params: { id: merchant.parent_account_id } }">
            {{ merchant.parent_name }}
          </router-link>
          <span v-else>—</span>
        </v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Website:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.website }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Manager Password:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.manager_password }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Timezone:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.timezone }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Enabled:</v-flex>
        <v-flex xs6 sm4 class="pa-1">
          <span class="status-pill" :class="!merchant.is_active ? 'error lighten-5 pa-1 error--text text--darken-3 font-weight-black' : 'success lighten-5 pa-1 success--text text--darken-4 font-weight-black'">
            <v-icon v-if="!merchant.is_active" small class="mr-1">fa-times</v-icon>
            <v-icon v-if="merchant.is_active" small class="mr-1">fa-check</v-icon>
            {{ merchant.is_active ? "Yes" : "No" }}<span class="sr-only">{{ merchant.is_active ? " (Account Enabled)" : " (Account Disabled)" }}</span>
          </span>
        </v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Cash Discount:</v-flex>
        <v-flex xs6 sm4 class="pa-1">
          <span class="status-pill" :class="!merchant.is_cashdiscount ? 'error lighten-5 pa-1 error--text text--darken-3 font-weight-black' : 'success lighten-5 pa-1 success--text text--darken-4 font-weight-black'">
            <v-icon v-if="!merchant.is_cashdiscount" small class="mr-1">fa-times</v-icon>
            <v-icon v-if="merchant.is_cashdiscount" small class="mr-1">fa-check</v-icon>
            {{ merchant.is_cashdiscount ? "Yes" : "No" }}<span class="sr-only">{{ merchant.is_cashdiscount ? " (Cash Discount Enabled)" : " (Cash Discount Disabled)" }}</span>
          </span>
        </v-flex>

        <template v-if="merchant.is_cashdiscount">
          <template v-if="merchant.fpg_cashdiscount_schedule !== null && merchant.fpg_cashdiscount_schedule !== undefined">
            <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">FPG Cash Discount Schedule:</v-flex>
            <v-flex xs6 sm4 class="pa-1">{{ merchant.fpg_cashdiscount_schedule || '—' }}</v-flex>
          </template>
          <template v-if="merchant.pnx_cashdiscount_schedule !== null && merchant.pnx_cashdiscount_schedule !== undefined">
            <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">PNX Cash Discount Schedule:</v-flex>
            <v-flex xs6 sm4 class="pa-1">{{ merchant.pnx_cashdiscount_schedule || '—' }}</v-flex>
          </template>
        </template>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">ISO:</v-flex>
        <v-flex xs6 sm4 class="pa-1" :class="merchant.is_iso ? 'success--text font-weight-medium' : ''">
          {{ merchant.is_iso ? "Yes" : "No" }}
        </v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Created:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ datetime(merchant.created) }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Modified:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ datetime(merchant.updated) }}</v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap class="detail-grid">
        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Address 1:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.street_1 }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Address 2:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.street_2 }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">City:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.city }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">State:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.state }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Postal Code:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.zip }}</v-flex>

        <v-flex xs6 sm2 class="primary--text font-weight-black pa-1">Phone:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ phone(merchant.phone) }}</v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap class="detail-grid">
        <v-flex xs12 class="primary--text font-weight-black pa-1">Receipt Headers</v-flex>
        <v-flex xs6 sm2 class="primary--text pa-1">Header 1:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.receipt_header_1 }}</v-flex>
        <v-flex xs6 sm2 class="primary--text pa-1">Header 2:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.receipt_header_2 }}</v-flex>
        <v-flex xs6 sm2 class="primary--text pa-1">Header 3:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.receipt_header_3 }}</v-flex>

        <v-flex xs12 class="primary--text font-weight-black pa-1 mt-2">Receipt Footers</v-flex>
        <v-flex xs6 sm2 class="primary--text pa-1">Footer 1:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.receipt_footer_1 }}</v-flex>
        <v-flex xs6 sm2 class="primary--text pa-1">Footer 2:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.receipt_footer_2 }}</v-flex>
        <v-flex xs6 sm2 class="primary--text pa-1">Footer 3:</v-flex>
        <v-flex xs6 sm4 class="pa-1">{{ merchant.receipt_footer_3 }}</v-flex>
      </v-layout>
    </template>

    <!-- Edit mode: proper form layout with box inputs -->
    <template v-else>
      <v-layout row wrap>
        <v-flex xs12 px-2>
          <h3 class="pa-2 primary--text">Basic Information</h3>
        </v-flex>

        <v-flex xs12 sm6 lg4 px-2>
          <v-text-field label="Name" v-model.trim="merchant.name" box required :rules="[v => !!v || 'Name is required']"></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg4 px-2>
          <v-text-field label="Email" v-model.trim="merchant.email" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg4 px-2>
          <v-text-field label="Contact Name" v-model.trim="merchant.contact_name" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg4 px-2>
          <v-text-field label="Website" v-model.trim="merchant.website" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg4 px-2>
          <v-text-field label="Manager Password" v-model.trim="merchant.manager_password" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg4 px-2>
          <v-select
            label="Timezone"
            v-model="merchant.timezone"
            :items="timezoneOptions"
            item-text="text"
            item-value="value"
            box
            clearable
          ></v-select>
        </v-flex>
        <v-flex xs12 sm6 lg4 px-2>
          <v-text-field label="Phone" v-model.trim="merchant.phone" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg4 px-2>
          <v-select
            label="Sales Representatives"
            v-model="selectedSalesRepIds"
            :items="salesReps"
            item-text="name"
            item-value="id"
            box
            clearable
            multiple
            chips
            small-chips
          ></v-select>
        </v-flex>
        <v-flex xs12 sm6 lg4 px-2 class="d-flex align-center">
          <v-checkbox label="ISO" v-model="merchant.is_iso" color="primary" hide-details></v-checkbox>
        </v-flex>
        <v-flex xs12 sm6 lg4 px-2 class="d-flex align-center">
          <v-checkbox label="Cash Discount" v-model="merchant.is_cashdiscount" color="primary" hide-details class="mr-4" style="flex-shrink:0"></v-checkbox>
        </v-flex>
        <v-flex v-if="merchant.fpg_cashdiscount_schedule !== null && merchant.fpg_cashdiscount_schedule !== undefined" xs12 sm6 lg4 px-2>
          <v-select
            label="FPG Cash Discount Schedule"
            v-model="merchant.fpg_cashdiscount_schedule"
            :items="cashDiscountSchedules"
            :disabled="!merchant.is_cashdiscount"
            box
            clearable
            hide-details
          ></v-select>
        </v-flex>
        <v-flex v-if="merchant.pnx_cashdiscount_schedule !== null && merchant.pnx_cashdiscount_schedule !== undefined" xs12 sm6 lg4 px-2>
          <v-select
            label="PNX Cash Discount Schedule"
            v-model="merchant.pnx_cashdiscount_schedule"
            :items="cashDiscountSchedules"
            :disabled="!merchant.is_cashdiscount"
            box
            clearable
            hide-details
          ></v-select>
        </v-flex>

        <v-flex xs12 mt-2 px-2>
          <h3 class="pa-2 primary--text">Address</h3>
        </v-flex>
        <v-flex xs12 sm6 px-2>
          <v-text-field label="Address 1" v-model.trim="merchant.street_1" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 px-2>
          <v-text-field label="Address 2" v-model.trim="merchant.street_2" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg3 px-2>
          <v-text-field label="City" v-model.trim="merchant.city" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm4 lg2 px-2>
          <v-text-field label="State" v-model.trim="merchant.state" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm4 lg2 px-2>
          <v-text-field label="Postal Code" v-model.trim="merchant.zip" box></v-text-field>
        </v-flex>

        <v-flex xs12 mt-2 px-2>
          <h3 class="pa-2 primary--text">Receipt Headers</h3>
        </v-flex>
        <v-flex xs12 sm4 px-2>
          <v-text-field label="Header 1" v-model.trim="merchant.receipt_header_1" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm4 px-2>
          <v-text-field label="Header 2" v-model.trim="merchant.receipt_header_2" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm4 px-2>
          <v-text-field label="Header 3" v-model.trim="merchant.receipt_header_3" box></v-text-field>
        </v-flex>

        <v-flex xs12 mt-2 px-2>
          <h3 class="pa-2 primary--text">Receipt Footers</h3>
        </v-flex>
        <v-flex xs12 sm4 px-2>
          <v-text-field label="Footer 1" v-model.trim="merchant.receipt_footer_1" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm4 px-2>
          <v-text-field label="Footer 2" v-model.trim="merchant.receipt_footer_2" box></v-text-field>
        </v-flex>
        <v-flex xs12 sm4 px-2>
          <v-text-field label="Footer 3" v-model.trim="merchant.receipt_footer_3" box></v-text-field>
        </v-flex>
      </v-layout>
    </template>

    <v-divider class="primary"></v-divider>

    <v-expansion-panel v-model="panel" expand multiple class="elevation-0">
      <v-expansion-panel-content>
        <div slot="header" class="title font-weight-medium">Notes</div>
        <v-card>
          <v-card-text>
            <span v-if="!editMode" class="pre-wrap">{{ merchant.notes }}</span>
            <v-textarea v-else v-model="merchant.notes" box auto-grow></v-textarea>
          </v-card-text>
        </v-card>
      </v-expansion-panel-content>

      <v-expansion-panel-content>
        <div slot="header" class="title font-weight-medium">Permissions</div>
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="[
                { text: 'Permission', value: 'key', sortable: true },
                { text: 'Description', value: 'description', sortable: false },
                { text: 'Value', value: 'value', sortable: false },
                { text: '', value: 'actions', sortable: false, align: 'right' }
              ]"
              :items="permissionsEntries"
              hide-actions
              class="elevation-1"
            >
              <template v-slot:items="props">
                <td>{{ props.item.key }}</td>
                <td class="grey--text">{{ props.item.description }}</td>
                <td>
                  <template v-if="typeof props.item.value === 'boolean'">
                    <v-chip small :color="props.item.value ? 'success' : 'error'" text-color="white">
                      {{ props.item.value ? 'Yes' : 'No' }}
                    </v-chip>
                  </template>
                  <template v-else-if="Array.isArray(props.item.value)">
                    <code>{{ props.item.value.join(', ') }}</code>
                  </template>
                  <template v-else-if="props.item.value !== null && typeof props.item.value === 'object'">
                    <code>{{ JSON.stringify(props.item.value) }}</code>
                  </template>
                  <template v-else>
                    {{ props.item.value }}
                  </template>
                </td>
                <td class="text-xs-right">
                  <v-btn small flat color="error" @click="removePermission(props.item.key)">
                    <v-icon small left>fa-trash</v-icon>
                    Remove
                  </v-btn>
                </td>
              </template>
              <template v-slot:no-data>
                <v-alert :value="true" color="primary">No permissions configured for this account</v-alert>
              </template>
            </v-data-table>

            <v-layout row wrap justify-space-between align-center mt-3>
              <v-btn outline color="primary" @click="addPermissionDialog = true">
                <v-icon small left>fa-plus</v-icon>
                Add Permission
              </v-btn>
              <v-btn color="primary" :loading="savingPermissions" @click="savePermissions">
                Save Permissions
              </v-btn>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-expansion-panel-content>

      <v-expansion-panel-content>
        <div slot="header" class="title font-weight-medium">HPP Receipt Copy Emails</div>
        <v-card>
          <v-card-text>
            <p class="caption grey--text mb-2">BCC addresses that receive a copy of every HPP transaction receipt.</p>
            <v-data-table
              :headers="[
                { text: 'Email Address', value: 'email', sortable: false },
                { text: '', value: 'actions', sortable: false, align: 'right' }
              ]"
              :items="hppReceiptEmailItems"
              hide-actions
              class="elevation-1 mb-3"
            >
              <template v-slot:items="props">
                <td>{{ props.item }}</td>
                <td class="text-xs-right">
                  <v-btn small flat color="error" @click="removeHppEmail(props.item)">
                    <v-icon small left>fa-trash</v-icon>
                    Remove
                  </v-btn>
                </td>
              </template>
              <template v-slot:no-data>
                <v-alert :value="true" color="primary">No BCC emails configured</v-alert>
              </template>
            </v-data-table>
            <v-layout row wrap justify-space-between align-center>
              <v-btn outline color="primary" @click="hppEmailDialog = true">
                <v-icon small left>fa-plus</v-icon>
                Add Email
              </v-btn>
              <v-btn color="primary" :loading="savingHppEmails" @click="saveHppEmails">
                Save
              </v-btn>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-expansion-panel-content>

      <!-- Add Permission dialog -->
      <v-dialog v-model="addPermissionDialog" max-width="450" persistent>
        <v-card>
          <v-card-title class="headline">Add Permission</v-card-title>
          <v-card-text>
            <v-select
              v-model="pendingPerm.key"
              :items="availableAccountPermissions"
              item-text="label"
              item-value="key"
              label="Permission"
              box
              autofocus
            >
              <template v-slot:item="data">
                <v-list-tile-content>
                  <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ data.item.description }}</v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
            </v-select>

            <!-- Boolean input -->
            <v-select
              v-if="selectedPermissionType === 'boolean'"
              v-model="pendingPerm.value"
              :items="[{ text: 'Yes', value: true }, { text: 'No', value: false }]"
              label="Value"
              box
            ></v-select>

            <!-- Number input -->
            <v-text-field
              v-else-if="selectedPermissionType === 'number'"
              v-model.number="pendingPerm.value"
              label="Value"
              type="number"
              step="0.01"
              hint="Enter as a percent (e.g., 2.5 for 2.5%)"
              persistent-hint
              box
            ></v-text-field>

            <!-- String input -->
            <v-text-field
              v-else-if="selectedPermissionType === 'string'"
              v-model="pendingPerm.value"
              label="Value"
              box
            ></v-text-field>

            <!-- Array input (comma-separated) -->
            <v-text-field
              v-else-if="selectedPermissionType === 'array'"
              v-model="pendingPerm.value"
              label="Value"
              hint="Enter comma-separated values (e.g., USD,CAD,EUR)"
              persistent-hint
              box
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn flat @click="closeAddPermissionDialog">Cancel</v-btn>
            <v-btn color="primary" :disabled="!pendingPerm.key || pendingPerm.value === null || pendingPerm.value === ''" @click="addPermission">Add Permission</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Add HPP Email dialog -->
      <v-dialog v-model="hppEmailDialog" max-width="450" persistent>
        <v-card>
          <v-card-title class="headline">Add BCC Email</v-card-title>
          <v-card-text>
            <v-text-field
              v-model.trim="pendingHppEmail"
              label="Email Address"
              box
              autofocus
              @keyup.enter="addHppEmail"
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn flat @click="hppEmailDialog = false; pendingHppEmail = ''">Cancel</v-btn>
            <v-btn color="primary" :disabled="!pendingHppEmail" @click="addHppEmail">Add Email</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-expansion-panel-content>
        <div slot="header" class="title font-weight-medium">MIDS</div>
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="[
                { text: 'Type', value: 'type', sortable: true },
                { text: 'MID', value: 'mid', sortable: true }
              ]"
              :items="mids"
              hide-actions
              class="elevation-1"
            >
              <template v-slot:items="props">
                <td>{{ props.item.type === 'pnx' ? 'PNX' : 'FPG' }}</td>
                <td>{{ props.item.mid }}</td>
              </template>
              <template v-slot:no-data>
                <v-alert :value="true" color="primary">No MIDs found for this account.</v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-expansion-panel-content>

      <v-expansion-panel-content>
        <div slot="header" class="title font-weight-medium">Terminals</div>
        <Terminals ref="terminals" :accountId="merchant.id" v-if="merchant.id"></Terminals>
      </v-expansion-panel-content>

      <v-expansion-panel-content>
        <div slot="header" class="title font-weight-medium">Users</div>
        <Users ref="users" :accountId="merchant.id" v-if="merchant.id"></Users>
      </v-expansion-panel-content>

      <v-expansion-panel-content>
        <div slot="header" class="title font-weight-medium">Chargebacks</div>
        <Chargebacks ref="chargebacks" :accountId="merchant.id" v-if="merchant.id"></Chargebacks>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-layout row wrap class="mt-5">
      <v-flex v-if="hasPermission('superuser')">
        <div class="d-flex justify-start">
          <v-btn
            style="max-width: 275px;"
            round
            @click="migrateFunction"
            color="info"
          >
            Migrate Merchant
          </v-btn>
        </div>
      </v-flex>
      <v-flex xs6>
        <div class="d-flex justify-end">
          <v-btn
            v-if="merchant.is_active"
            style="max-width: 275px;"
            round
            @click="closeMerchant"
            color="error"
          >
            <v-icon class="mr-2 error--text text--darken-4">fa-times</v-icon>
            Close Merchant Account
          </v-btn>
          <v-btn
            v-if="!merchant.is_active"
            style="max-width: 275px;"
            round
            @click="reopenMerchant"
            color="success"
          >
            <v-icon class="mr-2 success--text text--darken-4">fa-check</v-icon>
            Reopen Merchant Account
          </v-btn>
        </div>
      </v-flex>
    </v-layout>

  </v-container>
</template>

<script>
import Bus from '@/bus'

import Chargebacks from '@/components/transactions/Chargebacks'
import Terminals from '@/components/manage/Terminals'
import Users from '@/components/manage/Users'
import MerchantImageManagment from './MerchantImageManagement.vue'

import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import CashDiscountMixin from '@/mixins/cashDiscount'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'

export default {
  components: {
    Chargebacks,
    Terminals,
    Users,
    MerchantImageManagment
  },
  mixins: [ApiMixin, AuthMixin, CashDiscountMixin, DisplayMixin, StoreMixin],

  data: function () {
    return {
      panel: [false, false, false, false, false, false, false],
      merchant: {},
      originalMerchant: {},
      mids: [],
      terminals: [],
      users: [],
      merchantLoaded: false,
      salesReps: [],
      selectedSalesRepIds: [],
      originalSalesRepIds: [],
      editMode: false,
      savingPermissions: false,
      savingHppEmails: false,
      addPermissionDialog: false,
      hppEmailDialog: false,
      pendingPerm: { key: '', value: true },
      pendingHppEmail: '',
      timezoneOptions: [
        { header: 'United States' },
        { text: 'Eastern Time (America/New_York)', value: 'America/New_York' },
        { text: 'Central Time (America/Chicago)', value: 'America/Chicago' },
        { text: 'Mountain Time (America/Denver)', value: 'America/Denver' },
        { text: 'Mountain Time – no DST (America/Phoenix)', value: 'America/Phoenix' },
        { text: 'Pacific Time (America/Los_Angeles)', value: 'America/Los_Angeles' },
        { text: 'Alaska Time (America/Anchorage)', value: 'America/Anchorage' },
        { text: 'Hawaii Time (Pacific/Honolulu)', value: 'Pacific/Honolulu' },
        { text: 'Atlantic Time (America/Puerto_Rico)', value: 'America/Puerto_Rico' },
        { divider: true },
        { header: 'UTC' },
        { text: 'UTC', value: 'UTC' },
        { text: 'Etc/UTC', value: 'Etc/UTC' },
        { divider: true },
        { header: 'Europe' },
        { text: 'London (Europe/London)', value: 'Europe/London' },
        { text: 'Paris / Berlin (Europe/Paris)', value: 'Europe/Paris' },
        { text: 'Helsinki / Kyiv (Europe/Helsinki)', value: 'Europe/Helsinki' },
        { divider: true },
        { header: 'Americas' },
        { text: 'Toronto (America/Toronto)', value: 'America/Toronto' },
        { text: 'Vancouver (America/Vancouver)', value: 'America/Vancouver' },
        { text: 'Mexico City (America/Mexico_City)', value: 'America/Mexico_City' },
        { text: 'São Paulo (America/Sao_Paulo)', value: 'America/Sao_Paulo' },
        { divider: true },
        { header: 'Asia / Pacific' },
        { text: 'Dubai (Asia/Dubai)', value: 'Asia/Dubai' },
        { text: 'Mumbai (Asia/Kolkata)', value: 'Asia/Kolkata' },
        { text: 'Bangkok (Asia/Bangkok)', value: 'Asia/Bangkok' },
        { text: 'Singapore (Asia/Singapore)', value: 'Asia/Singapore' },
        { text: 'Tokyo (Asia/Tokyo)', value: 'Asia/Tokyo' },
        { text: 'Shanghai / Beijing (Asia/Shanghai)', value: 'Asia/Shanghai' },
        { text: 'Sydney (Australia/Sydney)', value: 'Australia/Sydney' },
        { text: 'Auckland (Pacific/Auckland)', value: 'Pacific/Auckland' }
      ],
      availableAccountPermissions: [
        // Boolean permissions
        { key: 'can_standalone_refund', type: 'boolean', label: 'Standalone Refund', description: 'Allows blind credits/refunds without an original transaction' },
        { key: 'can_bypass_cvv_validation', type: 'boolean', label: 'Bypass CVV Validation', description: 'Skips CVV2 requirement during card validation' },
        { key: 'can_use_nacha_authorization', type: 'boolean', label: 'NACHA Authorization', description: 'Enables ACH NACHA authorization type' },
        { key: 'has_hidden_cash_discount', type: 'boolean', label: 'Hidden Cash Discount', description: 'Hides the cash discount line item on receipts/emails' },
        { key: 'can_waive_card_fees', type: 'boolean', label: 'Can Waive Card Fees', description: 'Allows waiving cash discount fees on card transactions' },
        { key: 'can_waive_health_card_fees', type: 'boolean', label: 'Can Waive HSA Card Fees', description: 'Allows waiving cash discount fees on HSA card transactions' },
        { key: 'can_waive_debit_fees', type: 'boolean', label: 'Can Waive Debit Fees', description: 'Allows waiving cash discount fees on debit card transactions' },
        { key: 'can_use_hpp', type: 'boolean', label: 'Can Use Hosted Payment Page', description: 'Enables access to hosted payment page (HPP) functionality' },
        // Numeric permissions (fee rates)
        { key: 'waived_fee_rate', type: 'number', label: 'Waived Fee Rate', description: 'Fee rate for waived fee transactions (for reports, e.g., 2.5 for 2.5%)' },
        { key: 'waived_hsa_fee_rate', type: 'number', label: 'Waived HSA Fee Rate', description: 'Fee rate for HSA waived fee transactions (for reports, e.g., 3.5 for 3.5%)' },
        { key: 'waived_debit_fee_rate', type: 'number', label: 'Waived Debit Fee Rate', description: 'Fee rate for debit waived fee transactions (for reports, e.g., 2.6 for 2.6%)' },
        { key: 'ach_fee_rate', type: 'number', label: 'ACH Fee Rate', description: 'Fee rate for ACH transactions (for reports, e.g., 1 for 1%)' },
        // String permissions
        { key: 'notes', type: 'string', label: 'Permission Notes', description: 'Additional notes about account permissions' },
        // Array permissions
        { key: 'currencies', type: 'array', label: 'Allowed Currencies', description: 'Comma-separated list of allowed currencies (e.g., USD,CAD,EUR)' }
      ]
    }
  },

  computed: {
    parsedPermissions () {
      try {
        const perms = typeof this.merchant.permissions === 'string'
          ? JSON.parse(this.merchant.permissions)
          : this.merchant.permissions
        return (perms && typeof perms === 'object') ? perms : {}
      } catch (e) {
        return {}
      }
    },
    hppReceiptEmailItems () {
      const val = this.parsedPermissions['hpp_receipt_copy_email']
      if (!val) return []
      return val.split(',').map(e => e.trim()).filter(e => e)
    },
    permissionsEntries () {
      return Object.entries(this.parsedPermissions)
        .filter(([key]) => key !== 'hpp_receipt_copy_email')
        .map(([key, value]) => {
          const known = this.availableAccountPermissions.find(p => p.key === key)
          return { key, value, description: known ? known.description : '' }
        })
    },
    cashDiscountSchedules () {
      return Object.keys(this.feeSchedule)
    },
    selectedPermissionType () {
      if (!this.pendingPerm.key) return null
      const perm = this.availableAccountPermissions.find(p => p.key === this.pendingPerm.key)
      return perm ? perm.type : null
    }
  },

  watch: {
    'pendingPerm.key' (newKey) {
      // Reset value to appropriate default when permission type changes
      if (!newKey) {
        this.pendingPerm.value = true
        return
      }
      const perm = this.availableAccountPermissions.find(p => p.key === newKey)
      if (!perm) {
        this.pendingPerm.value = true
        return
      }
      // Set default value based on type
      switch (perm.type) {
        case 'boolean':
          this.pendingPerm.value = true
          break
        case 'number':
          this.pendingPerm.value = 0
          break
        case 'string':
          this.pendingPerm.value = ''
          break
        case 'array':
          this.pendingPerm.value = ''
          break
        default:
          this.pendingPerm.value = true
      }
    }
  },

  mounted () {
    this.load()
  },

  methods: {
    addHppEmail () {
      if (!this.pendingHppEmail) return
      const emails = [...this.hppReceiptEmailItems]
      if (!emails.includes(this.pendingHppEmail)) {
        emails.push(this.pendingHppEmail)
      }
      const perms = { ...this.parsedPermissions, hpp_receipt_copy_email: emails.join(',') }
      this.$set(this.merchant, 'permissions', perms)
      this.pendingHppEmail = ''
      this.hppEmailDialog = false
    },

    removeHppEmail (email) {
      const emails = this.hppReceiptEmailItems.filter(e => e !== email)
      const perms = { ...this.parsedPermissions }
      if (emails.length) {
        perms['hpp_receipt_copy_email'] = emails.join(',')
      } else {
        delete perms['hpp_receipt_copy_email']
      }
      this.$set(this.merchant, 'permissions', perms)
    },

    closeAddPermissionDialog () {
      this.addPermissionDialog = false
      this.pendingPerm = { key: '', value: true }
    },

    addPermission () {
      if (!this.pendingPerm.key) return

      // Handle different value types
      let value = this.pendingPerm.value
      const permType = this.selectedPermissionType

      // Convert array type from comma-separated string to array
      if (permType === 'array' && typeof value === 'string') {
        value = value.split(',').map(v => v.trim()).filter(v => v)
      }

      const perms = { ...this.parsedPermissions, [this.pendingPerm.key]: value }
      this.$set(this.merchant, 'permissions', perms)
      this.closeAddPermissionDialog()
    },

    removePermission (key) {
      const perms = { ...this.parsedPermissions }
      delete perms[key]
      this.$set(this.merchant, 'permissions', perms)
    },

    async _persistPermissions (successMessage) {
      await this.api({
        method: 'put',
        url: `/accounts/${this.merchant.id}`,
        data: {
          id: this.merchant.id,
          name: this.merchant.name,
          email: this.merchant.email,
          contact_name: this.merchant.contact_name,
          website: this.merchant.website,
          manager_password: this.merchant.manager_password,
          timezone: this.merchant.timezone,
          is_iso: this.merchant.is_iso ? 1 : 0,
          is_cashdiscount: this.merchant.is_cashdiscount ? 1 : 0,
          fpg_cashdiscount_schedule: this.merchant.fpg_cashdiscount_schedule !== undefined ? (this.merchant.fpg_cashdiscount_schedule || null) : undefined,
          pnx_cashdiscount_schedule: this.merchant.pnx_cashdiscount_schedule !== undefined ? (this.merchant.pnx_cashdiscount_schedule || null) : undefined,
          street_1: this.merchant.street_1,
          street_2: this.merchant.street_2,
          city: this.merchant.city,
          state: this.merchant.state,
          zip: this.merchant.zip,
          phone: this.merchant.phone,
          notes: this.merchant.notes,
          is_active: this.merchant.is_active,
          is_testing: this.merchant.is_testing,
          processor_id: this.merchant.processor_id,
          parent_account_id: this.merchant.parent_account_id,
          receipt_header_1: this.merchant.receipt_header_1,
          receipt_header_2: this.merchant.receipt_header_2,
          receipt_header_3: this.merchant.receipt_header_3,
          receipt_footer_1: this.merchant.receipt_footer_1,
          receipt_footer_2: this.merchant.receipt_footer_2,
          receipt_footer_3: this.merchant.receipt_footer_3,
          permissions: JSON.stringify(this.parsedPermissions)
        }
      })
      this.originalMerchant.permissions = this.merchant.permissions
      Bus.$emit('openModal', { status: 'success', message: successMessage })
    },

    async savePermissions () {
      this.savingPermissions = true
      try {
        await this._persistPermissions('Permissions saved')
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        this.savingPermissions = false
      }
    },

    async saveHppEmails () {
      this.savingHppEmails = true
      try {
        await this._persistPermissions('HPP Receipt Copy Emails saved')
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        this.savingHppEmails = false
      }
    },

    cancelAllEdits () {
      this.merchant = Object.assign({}, this.originalMerchant)
      this.editMode = false
    },
    async load () {
      Bus.$emit('openWaitModal')

      try {
        if (!this.$route.params.id) {
          throw new Error('Invalid merchant')
        }

        let merchants = await this.api({
          url: `/accounts/${this.$route.params.id}`
        })

        this.merchant = merchants.data.account
        this.originalMerchant = Object.assign({}, this.merchant)
        this.merchantLoaded = true

        let midsResult = await this.api({
          url: `/accounts/${this.$route.params.id}/mids`
        })
        this.mids = midsResult.data.mids

        const salesReps = await this.getAccountSalesReps()
        this.salesReps = salesReps.data.rows

        this.selectedSalesRepIds = this.merchant.sales_rep_ids
          ? String(this.merchant.sales_rep_ids).split(',').map(id => Number(id.trim())).filter(Number.isInteger)
          : []
        this.originalSalesRepIds = [...this.selectedSalesRepIds]
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        Bus.$emit('closeWaitModal')
      }
    },
    async getAccountSalesReps () {
      let result = await this.api({
        method: 'get',
        url: '/users',
        params: {
          filter: {
            position: 'Account Rep',
            enabled: '1'
          }
        }
      })
      return result
    },
    async modifyMerchant () {
      try {
        if (window.confirm('Do you wish to modify this account.')) {
          Bus.$emit('openWaitModal')
          await this.api({
            url: `/accounts/${this.merchant.id}`,
            method: 'put',
            data: {
              id: this.merchant.id,
              name: this.merchant.name,
              email: this.merchant.email,
              contact_name: this.merchant.contact_name,
              website: this.merchant.website,
              manager_password: this.merchant.manager_password,
              timezone: this.merchant.timezone,
              is_iso: this.merchant.is_iso ? 1 : 0,
              is_cashdiscount: this.merchant.is_cashdiscount ? 1 : 0,
              fpg_cashdiscount_schedule: this.merchant.fpg_cashdiscount_schedule !== undefined ? (this.merchant.fpg_cashdiscount_schedule || null) : undefined,
              pnx_cashdiscount_schedule: this.merchant.pnx_cashdiscount_schedule !== undefined ? (this.merchant.pnx_cashdiscount_schedule || null) : undefined,
              street_1: this.merchant.street_1,
              street_2: this.merchant.street_2,
              city: this.merchant.city,
              state: this.merchant.state,
              zip: this.merchant.zip,
              phone: this.merchant.phone,
              notes: this.merchant.notes,
              is_active: this.merchant.is_active,
              is_testing: this.merchant.is_testing,
              processor_id: this.merchant.processor_id,
              parent_account_id: this.merchant.parent_account_id,
              receipt_header_1: this.merchant.receipt_header_1,
              receipt_header_2: this.merchant.receipt_header_2,
              receipt_header_3: this.merchant.receipt_header_3,
              receipt_footer_1: this.merchant.receipt_footer_1,
              receipt_footer_2: this.merchant.receipt_footer_2,
              receipt_footer_3: this.merchant.receipt_footer_3
            }
          })
          const sortedSelected = [...this.selectedSalesRepIds].sort().join(',')
          const sortedOriginal = [...this.originalSalesRepIds].sort().join(',')
          if (sortedSelected !== sortedOriginal) {
            await this.api({
              url: `/accounts/${this.merchant.id}/modify`,
              method: 'put',
              data: {
                sales_rep_ids: this.selectedSalesRepIds
              }
            })
          }
        }
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        Bus.$emit('closeWaitModal')
      }

      try {
        this.editMode = false
        this.merchantLoaded = false
        await this.load()
        this.merchantLoaded = true
      } catch (error) {
        console.error('Error Refreshing Merchant Data: ', error)
      }
    },
    async closeMerchant () {
      try {
        if (window.confirm('Do you wish to close this account.')) {
          Bus.$emit('openWaitModal')
          await this.api({
            url: `/accounts/${this.merchant.id}/close`,
            method: 'put',
            data: {
              id: this.merchant.id
            }
          })
        }
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        Bus.$emit('closeWaitModal')
      }

      try {
        this.merchantLoaded = false
        await this.load()
        this.merchantLoaded = true
      } catch (error) {
        console.error('Error Refreshing Merchant Data: ', error)
      }
    },
    async reopenMerchant () {
      try {
        if (window.confirm('Do you wish to reopen this account?')) {
          Bus.$emit('openWaitModal')
          await this.api({
            url: `/accounts/${this.merchant.id}/reopen`,
            method: 'put',
            data: {
              id: this.merchant.id
            }
          })
        }
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        Bus.$emit('closeWaitModal')
      }

      try {
        this.merchantLoaded = false
        await this.load()
        this.merchantLoaded = true
      } catch (error) {
        console.error('Error Refreshing Merchant Data: ', error)
      }
    },
    async migrateFunction () {
      const hasPnxAccount = this.mids.some(mid => mid.type === 'pnx')
      if (hasPnxAccount) {
        Bus.$emit('openModal', {
          status: 'error',
          message: 'A PayNetworx account already exists for this merchant.'
        })
        return
      }

      let result = await this.api({
        method: 'post',
        url: `/accounts/migrate/${this.merchant.id}`,
        data: {}
      })
      return result
    }
  }
}
</script>

<style lang="less">
.v-expansion-panel__container {
  margin: 5px;
}
.v-expansion-panel__header {
  background: var(--v-secondary-base) !important;
  padding: 5px;
}

.overflow-button {
  padding-bottom: 20px;
  padding-left: 5px;
  padding-right: 5px;
  padding-top: 20px;
}

.overflow-button .v-btn__content span {
  overflow: hidden;
  text-overflow: ellipsis !important;
  white-space: nowrap;
}

.detail-grid > div {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.status-pill {
  border-radius: 28px;
  display: inline-flex;
  align-items: center;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.pre-wrap {
  white-space: pre-wrap;
}
</style>



// WEBPACK FOOTER //
// src/components/manage/Merchant.vue